import React, {useEffect, useMemo} from 'react';
import {Redirect, Route, Switch, withRouter} from "react-router-dom";
import {useBootstrap, useEntities, useFilters, useProgress} from "@atttomyx/shared-hooks";
import {usePushEvent} from "@atttomyx/react-hooks";
import {
    AboutPage,
    ChangePasswordPage,
    ForgotPasswordPage,
    LoginPage,
    NewUserPage,
    NotFoundPage,
    NotificationsPage,
    PhoneStatusBar,
    ProfilePage,
    PublicHeader,
    Splash,
    UnderConstruction,
    UserSettingsPage,
    WelcomePage,
} from "@atttomyx/react-components";
import {UserSettingsForm} from "./components/forms";
import {Footer} from "./components/nav";
import {PeoplePage} from "./components/pages";
import {
    accountService,
    appService,
    authService,
    edgeService,
    mobileService,
    nodeService,
    notificationService,
    profileService,
} from "./services";
import {notifications as notificationUtils, sorting, strings} from "@atttomyx/shared-utils";
import {branding as brandingUtils, firebase as firebaseUtils, mobile, router, timezones} from "@atttomyx/react-utils";
import {getNodeFilters} from "./filters";
import {themes, time} from "@atttomyx/shared-constants";
import {
    APP_NAME,
    APP_TAG_LINE,
    FIREBASE_CONFIG,
    NOTIFICATION_TYPES,
    PAGE_ABOUT,
    PAGE_CALENDAR,
    PAGE_HOME,
    PAGE_LOGIN,
    PAGE_NEW_USER,
    PAGE_NOTIFICATIONS,
    PAGE_OPTIONS,
    PAGE_PASSWORD,
    PAGE_PEOPLE,
    PAGE_PROFILE,
    PAGE_RECOVERY,
    PAGE_WELCOME,
    PATH_DEEP_LINK,
    PRIVATE_PAGES_EXACT,
    PRIVATE_PAGES_STARTS_WITH,
    PUBLIC_PAGES_EXACT,
    PUBLIC_PAGES_STARTS_WITH,
    TOPICS,
} from "./constants";
import icon from "./icon.png";

const MOCK_PHONE = false;

const App = (props) => {
    const { snackbar, dimensions, history, onThemeChange } = props;

    const notifications = useEntities(notificationService.listNotifications, snackbar.setError, "notifications", sorting.sortByCreatedDesc);
    const nodes = useEntities(nodeService.listNodes, snackbar.setError, "nodes", sorting.sortByUpdatedDesc);
    const edges = useEntities(edgeService.listEdges, snackbar.setError, "edges", sorting.sortByUpdatedDesc);

    const keyToMetadata = useMemo(() => {
        return getNodeFilters(nodes.entities, edges.entities);
    }, [nodes.entities, edges.entities]);

    const nodeFilters = useFilters(keyToMetadata);

    const clearAll = () => {
        progress.clear();
        bootstrap.clear();
        notifications.clear();
        nodes.clear();
        edges.clear();
    };

    const onLogout = () => {
        authService.cancelAllRequests("logged out");
        authService.clearAuthToken();
        timezones.clearTimeZone();

        clearAll();

        router.redirectToInitialPage(history, PUBLIC_PAGES_EXACT, PUBLIC_PAGES_STARTS_WITH, PAGE_LOGIN);

        if (mobile.isMobile()) {
            mobile.lifecycle(true, false);
        }
    };

    const bootstrapSuccess = () => {
        if (bootstrap.user.temporary) {
            router.redirectTo(history, PAGE_PASSWORD);

        } else {
            router.redirectToInitialPage(history, PRIVATE_PAGES_EXACT, PRIVATE_PAGES_STARTS_WITH, PAGE_HOME);
        }

        notifications.refresh();
        nodes.refresh();
        edges.refresh();

        if (mobile.isMobile()) {
            mobile.lifecycle(true, true);
        }

        authService.recordLoginIfNecessary(snackbar.setError);
    };

    const bootstrapFailure = (err) => {
        snackbar.setError(err);
        onLogout();
    };

    const bootstrap = useBootstrap(authService, accountService, profileService, notificationService,
        bootstrapSuccess, bootstrapFailure);

    const progress = useProgress(bootstrap,
        [notifications, nodes, edges],
        [],
        0,
        time.MILLISECONDS_IN_SECOND);

    const event = usePushEvent(bootstrap);

    const onLogin = (accounts) => {
        progress.refresh();
        bootstrap.refresh(accounts);
    };

    useEffect(() => {
        if (event && event.type) {
            const type = event.type;
            const data = event.payload || {};
            const payload = data.payload || {};
            const actor = data.actor || {};

            console.table(type, data);

            if (bootstrap.user.id !== actor.userId) {
                switch (type) {
                    case "TBD":
                        // todo: do stuff
                    default:
                        break;
                }

                if (notificationUtils.isEnabled(bootstrap.user, type)) {
                    const title = data.title;

                    if (strings.isNotBlank(title)) {
                        snackbar.setInfo(title);
                    }

                    if (notificationUtils.isPersist(bootstrap.user, type)) {
                        notifications.refresh();
                    }
                }
            }
        }
    }, [event]);

    useEffect(() => {
        const settings = bootstrap.user ? bootstrap.user.settings || {} : {};
        const theme = settings.theme || themes.LIGHT;
        const branding = brandingUtils.getBranding(theme);

        if (mobile.isMobile()) {
            mobile.setBranding(theme, branding);
        }

        onThemeChange(theme, branding);
    }, [bootstrap.account, bootstrap.user]);

    useEffect(() => {
        if (mobile.isMobile()) {
            mobile.syncProgress(progress.percent, progress.loading);
        }
    }, [progress.percent, progress.loading]);

    useEffect(() => {
        if (mobile.isMobile()) {
            mobileService.initMessagingTokenObserver(bootstrap);
        }
    }, [bootstrap]);

    useEffect(() => {
        appService.ensureOnLatestVersion(false, null);
        authService.setupAxiosInterceptors(onLogout);
        notificationUtils.initNotifications(NOTIFICATION_TYPES, TOPICS);
        firebaseUtils.initFirebase(FIREBASE_CONFIG);
        dimensions.onResize();

        if (mobile.isMobile()) {
            mobile.initializeMessageListener();
        }

        if (authService.loggedIn()) {
            onLogin();

        } else {
            onLogout();
        }

        return () => {
            if (mobile.isMobile()) {
                mobile.deactivateMessageListener();
            }
        }
    }, []);

    return <div className="app">
        {progress.loading || progress.paused ?
            <Splash
                percent={progress.percent}
            /> : bootstrap.account && bootstrap.user ?
            <>
                {MOCK_PHONE ? <PhoneStatusBar/> : null}
                <div className="content">
                    <Switch>
                        <Route exact path="/">
                            <Redirect to={PAGE_HOME}/>
                        </Route>
                        <Route path={PAGE_NOTIFICATIONS}>
                            <NotificationsPage
                                snackbar={snackbar}
                                dimensions={dimensions}
                                notifications={notifications.entities}
                                onBulkSave={notifications.onEntitiesSaved}
                                onDelete={notifications.onEntityDeleted}
                                notificationService={notificationService}
                            />
                        </Route>
                        <Route path={PAGE_CALENDAR}>
                            <UnderConstruction
                                title="Calendar"
                            />
                        </Route>
                        <Route path={PAGE_PEOPLE}>
                            <PeoplePage
                                snackbar={snackbar}
                                dimensions={dimensions}
                                user={bootstrap.user}
                                nodes={nodes.entities}
                                edges={edges.entities}
                                filters={nodeFilters}
                                onSaveNode={nodes.onEntitySaved}
                                onSaveEdge={edges.onEntitySaved}
                                onDeleteNode={nodes.onEntityDeleted}
                                onDeleteEdge={edges.onEntityDeleted}
                            />
                        </Route>
                        <Route path={PAGE_PROFILE}>
                            <ProfilePage
                                snackbar={snackbar}
                                user={bootstrap.user}
                                onSaveProfile={bootstrap.syncProfile}
                                profileService={profileService}
                                showImage={true}
                                showBirthday={true}
                                onDeleteProfile={onLogout}
                            />
                        </Route>
                        <Route path={PAGE_OPTIONS}>
                            <UserSettingsPage
                                snackbar={snackbar}
                                user={bootstrap.user}
                                onSaveProfile={bootstrap.syncProfile}
                                onSaveNotifications={bootstrap.syncNotifications}
                                profileService={profileService}
                                notificationService={notificationService}
                                settingsForm={UserSettingsForm}
                                allowApp={true}
                                allowEmail={true}
                            />
                        </Route>
                        <Route path={PAGE_PASSWORD}>
                            <ChangePasswordPage
                                snackbar={snackbar}
                                user={bootstrap.user}
                                authService={authService}
                                onSaveProfile={bootstrap.syncProfile}
                                onToHome={() => router.redirectTo(history, PAGE_HOME)}
                            />
                        </Route>
                        <Route path={PAGE_ABOUT}>
                            <AboutPage
                                appName={APP_NAME}
                                appDescription={APP_TAG_LINE}
                                appService={appService}
                                user={bootstrap.user}
                                src={icon}
                            />
                        </Route>
                        <Route path={PAGE_LOGIN}>
                            <Redirect to={PAGE_HOME}/>
                        </Route>
                        <Route>
                            <NotFoundPage/>
                        </Route>
                    </Switch>
                </div>
                <Footer
                    dimensions={dimensions}
                    onLogout={onLogout}
                    numNotifications={notifications.entities.filter(notificationUtils.isNewNotification).length}
                />
            </> :
            <>
                <PublicHeader
                    dimensions={dimensions}
                    appName={APP_NAME}
                    src={icon}
                />
                <div className="content">
                    <Switch>
                        <Route path={PAGE_LOGIN}>
                            <LoginPage
                                snackbar={snackbar}
                                authService={authService}
                                onForgotPassword={() => router.redirectTo(history, PAGE_RECOVERY)}
                                onNewUser={() => router.redirectTo(history, PAGE_NEW_USER)}
                                onLogin={onLogin}
                            />
                        </Route>
                        <Route path={PAGE_RECOVERY}>
                            <ForgotPasswordPage
                                snackbar={snackbar}
                                authService={authService}
                                onBackToLogin={() => router.redirectTo(history, PAGE_LOGIN)}
                                onLogin={onLogin}
                                appName={APP_NAME}
                            />
                        </Route>
                        <Route path={`${PATH_DEEP_LINK}${PAGE_WELCOME}`}>
                            <WelcomePage
                                snackbar={snackbar}
                                authService={authService}
                                onToLogin={() => router.redirectTo(history, PAGE_LOGIN)}
                                onLogin={onLogin}
                            />
                        </Route>
                        <Route path={PAGE_NEW_USER}>
                            <NewUserPage
                                snackbar={snackbar}
                                authService={authService}
                                accountService={accountService}
                                onBackToLogin={() => router.redirectTo(history, PAGE_LOGIN)}
                                onLogin={onLogin}
                            />
                        </Route>
                    </Switch>
                </div>
            </>}
        </div>
}

export default withRouter(App);
